/** Reference catalogue. Every other locale must provide exactly these keys (enforced by the type). */
export const en = {
  'app.title': 'Sync Port · P2P file transfer',
  'secure.badge': 'End-to-end encrypted · device to device · nothing stored on any server',
  'secure.tooltip':
    'WebRTC data channels are encrypted with DTLS between the two devices. The server and the TURN relay only see ciphertext.',
  'error.title': 'Transmission lost',
  'error.peerFailed': 'The direct connection failed. Check the network or the TURN relay configuration.',
  'error.signaling': 'Could not reach the signaling server.',
  'error.roomNotFound': 'No open transfer matches this link or PIN. The sender may have closed the page, or it was mistyped.',
  'error.roomFull': 'Someone else already opened this link. Each link works for a single receiver.',
  'notFound.tagline': 'That coordinate is empty space.',
  'notFound.text': 'The page you opened does not exist.',
  'notFound.action': 'Send a file',
  'action.startOver': 'Start over',
  'action.retry': 'Retry',
  'action.cancel': 'Cancel',
  'action.dismiss': 'Dismiss',
  'sender.tagline': 'Beam files between devices over a private, encrypted link. Nothing stored in orbit.',
  'drop.title': 'Choose a file to beam',
  'drop.hint': 'Tap or drop a file here · any size',
  'share.title': 'Share this link',
  'share.text': 'Open it on the other device. Scan the code with a phone camera.',
  'share.linkLabel': 'Share link',
  'share.copy': 'Copy link',
  'share.native': 'Share…',
  'share.or': 'or',
  'share.pinHint': 'Enter this PIN on the start page of the other device.',
  'share.pinLabel': 'Transfer PIN',
  'share.copied': 'Copied to clipboard',
  'share.shared': 'Shared',
  'share.linkMessage': 'Open this link to receive a file from me:',
  'share.unavailable': 'This browser has no native share sheet.',
  'clipboard.blocked': 'Copy is blocked by this browser. Select the link and copy it manually.',
  'pin.title': 'Have a PIN or a QR code?',
  'pin.text': 'Type the 9-digit code shown on the sending device, or scan its QR code.',
  'pin.receive': 'Receive',
  'pin.scan': '📷 Scan QR code instead',
  'pin.incomplete': 'Enter all {count} digits of the PIN.',
  'scan.hint': 'Point the camera at the QR code on the sending device',
  'scan.notOurs': 'That code is not a Sync Port link. Keep looking for the one on the sending device.',
  'camera.denied': 'Camera access was denied. Allow it in the browser settings, or type the PIN instead.',
  'camera.notFound': 'No camera was found on this device.',
  'camera.busy': 'The camera is in use by another app.',
  'camera.failed': 'The camera could not be opened ({reason}).',
  'sender.status.creatingRoom': 'Creating a private room',
  'sender.status.waitingReceiver': 'Waiting for the receiver to open the link',
  'sender.status.receiverConnected': 'Receiver connected, opening an encrypted direct channel',
  'sender.status.waitingDestination': 'Encrypted channel open. Waiting for the receiver to choose where to save',
  'sender.status.beaming': 'Beaming file across space',
  'sender.status.delivered': 'Delivered',
  'status.failed': 'Transfer failed',
  'progress.sending': 'Sending',
  'progress.receiving': 'Receiving',
  'progress.sentDetail': 'Sent {sent} · Receiver confirmed {acked}',
  'progress.deliveredDetail': 'The receiver has the whole file.',
  'progress.arrivedDetail': 'Every byte arrived.',
  'progress.of': '{done} of {total}',
  'progress.speedEta': '{speed} · ETA {eta}',
  'route.finding': 'Finding the best route',
  'route.direct': 'Direct connection',
  'route.nat': 'Direct connection through NAT',
  'route.relay': 'Relayed through TURN',
  'route.relayOver': 'Relayed through TURN over {protocol}',
  'sender.sendAnother': 'Send another file',
  'receiver.tagline': 'Incoming transmission over a private, encrypted link. Keep this page open until the file lands.',
  'receiver.status.contacting': 'Contacting the sender',
  'receiver.status.connected': 'Connected to the sender, opening an encrypted direct channel',
  'receiver.status.channelOpen': 'Encrypted channel open, waiting for the file details',
  'receiver.status.chooseDestination': 'Choose where to save to start receiving',
  'receiver.status.receiving': 'Receiving',
  'receiver.status.complete': 'Complete',
  'dest.title': 'Ready to receive',
  'dest.picker.button': 'Choose where to save',
  'dest.picker.text': '{size} incoming. Pick a destination and the file streams straight into it.',
  'dest.worker.button': 'Save to this device',
  'dest.worker.text': '{size} incoming. It streams straight to your Downloads folder as it arrives.',
  'dest.memory.button': 'Receive file',
  'dest.memory.text': '{size} incoming. This browser keeps it in the tab until you save it afterwards.',
  'dest.cancelled': 'Pick a destination to start receiving.',
  'landed.title': 'Landed safely',
  'landed.chosenFolder': 'Saved as {name} in the folder you chose.',
  'landed.downloads': "Saved to your browser's Downloads folder.",
  'landed.openWith': 'Open with…',
  'landed.sendYourself': 'Send a file yourself',
  'memory.received': '{size} received. Save it to this device.',
  'memory.save': 'Save file',
  'memory.saved': "Saved to your browser's Downloads folder. You can also open it in another app right away.",
  'memory.saveAgain': 'Save again',
  'error.inboxNotFound': 'This inbox number does not exist.',
  'error.inboxUnauthorized': 'The key does not match this inbox.',
  'error.declined': 'The inbox owner declined this file.',
  'error.inboxLimit': 'No more inboxes can be created on this server.',
  'home.inboxLink': 'Create an inbox',
  'inbox.tagline': 'Your permanent inbox. Anyone who scans the code can offer you a file; you decide what to receive.',
  'inbox.create.title': 'Create an inbox',
  'inbox.create.text': 'Get a permanent number and QR code. Anyone who scans it can offer you a file; nothing arrives until you accept.',
  'inbox.create.button': 'Create my inbox',
  'inbox.connect.title': 'Already have an inbox?',
  'inbox.connect.text': 'Paste the secret key to use the inbox on this device. The number is optional: it can be found from the key.',
  'inbox.connect.numberOptional': 'Inbox number (optional)',
  'error.keyUnknown': 'No inbox matches this key.',
  'inbox.connect.number': 'Inbox number',
  'inbox.connect.key': 'Secret key',
  'inbox.connect.button': 'Connect',
  'inbox.key.title': 'Your secret key',
  'inbox.key.text': 'Keep the number and the key together somewhere safe. The key is shown only here and, with the number, is the only way to open this inbox on another device.',
  'inbox.key.copy': 'Copy number and key',
  'inbox.key.copied': 'Number and key copied',
  'inbox.key.done': 'I saved them',
  'inbox.number.title': 'Your inbox number',
  'inbox.number.text': 'Print this code or show it on a screen. Customers scan it, or type the number on the start page.',
  'inbox.status.connecting': 'Connecting to your inbox',
  'inbox.status.online': 'Online. Waiting for customers',
  'inbox.status.reconnecting': 'Connection lost. Reconnecting',
  'inbox.requests.title': 'Incoming requests',
  'inbox.requests.empty': 'No requests yet. They appear here the moment a customer sends a file.',
  'inbox.request.accept': 'Accept and save…',
  'inbox.request.decline': 'Decline',
  'inbox.request.connecting': 'Accepted. Connecting to the customer',
  'inbox.request.cancelled': 'The customer left before you answered.',
  'inbox.refresh': 'Refresh',
  'inbox.forget': 'Forget this inbox on this device',
  'toInbox.tagline': 'Send a file to inbox {number}. The owner sees your request and decides.',
  'toInbox.status.announcing': 'Announcing your file to the inbox',
  'toInbox.status.waitingOnline': 'Delivered to the inbox. Waiting for approval',
  'toInbox.status.waitingOffline': 'The inbox owner is not online right now. Your request waits until they return',
  'toInbox.status.accepted': 'Accepted. Opening an encrypted direct channel',
  'inbox.print': 'Print poster (PDF)',
  'poster.title': 'Send me your files',
  'poster.number': 'Inbox number',
  'poster.step1': 'Scan the code with your phone camera.',
  'poster.step2': 'Or open {link} and enter the number.',
  'poster.step3': 'Pick the file. We accept it right here at the counter.',
  'poster.footer': 'Sync Port · end-to-end encrypted, device to device. Nothing is stored on any server.',
  'install.title': 'Install Sync Port',
  'install.native': 'Add it to your home screen for quick access.',
  'install.ios': 'Tap Share, then "Add to Home Screen".',
  'install.button': 'Install',
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Readonly<Record<MessageKey, string>>;
